import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardTab = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState({
    totalQuotes: 0,
    openQuotes: 0,
    wonQuotes: 0,
    lostQuotes: 0,
    totalSalesValue: 0,
    conversionRate: 0,
    averageQuoteValue: 0,
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Get quotes data
      const { data: quotes, error: quotesError } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id);

      if (quotesError) throw quotesError;

      // Get sales data
      const { data: sales, error: salesError } = await supabase?.from('provider_sales')?.select('*')?.eq('provider_id', user?.id);

      if (salesError) throw salesError;

      // Calculate metrics
      const totalQuotes = quotes?.length || 0;
      const openQuotes = quotes?.filter(q => ['concept', 'verstuurd', 'in_overweging']?.includes(q?.status))?.length || 0;
      const wonQuotes = quotes?.filter(q => q?.status === 'gewonnen')?.length || 0;
      const lostQuotes = quotes?.filter(q => q?.status === 'verloren')?.length || 0;
      const totalSalesValue = sales?.reduce((sum, s) => sum + (parseFloat(s?.sale_amount) || 0), 0) || 0;
      const conversionRate = totalQuotes > 0 ? Math.round((wonQuotes / totalQuotes) * 100) : 0;
      const totalQuoteValue = quotes?.reduce((sum, q) => sum + (parseFloat(q?.quote_amount) || 0), 0) || 0;
      const averageQuoteValue = totalQuotes > 0 ? Math.round(totalQuoteValue / totalQuotes) : 0;

      setMetrics({
        totalQuotes,
        openQuotes,
        wonQuotes,
        lostQuotes,
        totalSalesValue,
        conversionRate,
        averageQuoteValue,
      });

      // Prepare monthly sales data
      const monthlyMap = {};
      sales?.forEach(sale => {
        const month = new Date(sale?.created_at)?.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' });
        monthlyMap[month] = (monthlyMap?.[month] || 0) + parseFloat(sale?.sale_amount || 0);
      });

      const monthlyChartData = Object.entries(monthlyMap)?.map(([month, value]) => ({
        month,
        verkopen: Math.round(value),
      }));

      setMonthlyData(monthlyChartData);

      // Prepare status distribution data
      const statusMap = {
        concept: { label: 'Concept', count: 0, color: '#94a3b8' },
        verstuurd: { label: 'Verstuurd', count: 0, color: '#3b82f6' },
        in_overweging: { label: 'In Overweging', count: 0, color: '#f59e0b' },
        gewonnen: { label: 'Gewonnen', count: 0, color: '#10b981' },
        verloren: { label: 'Verloren', count: 0, color: '#ef4444' },
      };

      quotes?.forEach(quote => {
        if (statusMap?.[quote?.status]) {
          statusMap[quote?.status].count++;
        }
      });

      const statusChartData = Object.entries(statusMap)
        ?.filter(([_, data]) => data?.count > 0)
        ?.map(([status, data]) => ({
          name: data?.label,
          value: data?.count,
          color: data?.color,
        }));

      setStatusData(statusChartData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      label: 'Totaal Offertes',
      value: metrics?.totalQuotes,
      icon: 'FileText',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      label: 'Open Offertes',
      value: metrics?.openQuotes,
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
    },
    {
      label: 'Gewonnen',
      value: metrics?.wonQuotes,
      icon: 'CheckCircle',
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      label: 'Conversie Percentage',
      value: `${metrics?.conversionRate}%`,
      icon: 'TrendingUp',
      color: 'text-info',
      bgColor: 'bg-info/10',
    },
    {
      label: 'Totale Verkoopwaarde',
      value: `€${metrics?.totalSalesValue?.toLocaleString('nl-NL')}`,
      icon: 'Euro',
      color: 'text-success',
      bgColor: 'bg-success/10',
    },
    {
      label: 'Gemiddelde Offerte',
      value: `€${metrics?.averageQuoteValue?.toLocaleString('nl-NL')}`,
      icon: 'Calculator',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6]?.map((i) => (
            <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-muted rounded w-1/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metricCards?.map((metric, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className={`${metric?.bgColor} p-3 rounded-lg`}>
                <Icon name={metric?.icon} size={24} className={metric?.color} />
              </div>
            </div>
            <h3 className="text-2xl font-heading font-bold text-foreground mb-1">{metric?.value}</h3>
            <p className="text-sm text-muted-foreground">{metric?.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Sales Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <span>Verkopen per Maand</span>
          </h3>
          {monthlyData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value) => `€${value?.toLocaleString('nl-NL')}`}
                />
                <Bar dataKey="verkopen" fill="#10b981" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              <p>Geen verkoopgegevens beschikbaar</p>
            </div>
          )}
        </div>

        {/* Status Distribution Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="PieChart" size={20} className="text-primary" />
            <span>Offerte Status Verdeling</span>
          </h3>
          {statusData?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              <p>Geen statusgegevens beschikbaar</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;